<template lang="pug">
  el-row#question-builder
    
    // Main Form
    el-form#form-builder(
      :model="question"
      :rules="rules"
      ref="question"
      label-position="top")

      // Question Title
      el-row(:gutter="80")
        el-col(:span="12")    
          el-form-item(prop="title")    
            label(:class="$style.label") Question Title
            el-input(
              placeholder="Enter Quiz Title"
              minlength="1"
              :maxlength="uiSettings.TITLE"
              v-model="question.title") 
        // Score
        el-col(:span="5" style="float: right;")            
          el-form-item(prop="score")
            label(:class="$style.label") Score
            el-input(
              placeholder="Enter the score"
              v-model="question.score")   

      // Question Text
      el-form-item(prop="text")
        label(:class="$style.label") Question Text
        rich-editor(
          minlength="1"
          :maxlength="uiSettings.CONTENT"
          v-model="question.text")     
      
      el-row
        el-col(:span="24")
          // Allow Mathematical Eqns. switch    
          el-form-item()
            el-switch(
              v-model="question.allow_math_equivs"
              active-color="#13ce66"
              inactive-color="#ff4949"
              active-text="Allow Mathematical Equivalents"
              style="float: right;")  

      el-row(:gutter="80")
        el-col(:span="12")
          // Correct Answer
          el-form-item(prop="correct_answer")
            label(:class="$style.label") Correct Answer
            math-quill-input(
              v-if="question.allow_math_equivs"
              v-model="question.correct_answer"
              minlength="1"
              :maxlength="uiSettings.SYMBOLS"
              :custom-symbols="custom_symbols || []"
              placeholder="Enter the correct answer") 
            el-input(
              v-else
              minlength="1"
              :maxlength="uiSettings.SYMBOLS"
              v-model="question.correct_answer"
              placeholder="Enter the correct answer")   
 
          // Lefthand symbol          
          el-form-item(prop="lefthand")
            label(:class="$style.label") Left-hand Side (Optional)     
            math-quill-input(
              v-model="question.lefthand"
              :maxlength="uiSettings.SYMBOLS"
              :custom-symbols="custom_symbols || []"
            )         

          // Righthand symbol
          el-form-item(prop="righthand")
            label(:class="$style.label") Right-hand Side (Optional)
            math-quill-input(
                v-model="question.righthand"
                :maxlength="uiSettings.SYMBOLS"
                :custom-symbols="custom_symbols || []"
            )

          // Tags
          el-form-item(
            v-if="$store.state.adminData.userId !== null"
            prop="tags")
            label(:class="$style.label") Tags
            br
            el-select(
              v-model="searchParams"
              multiple
              filterable
              reserve-keyword
              placeholder=""
              no-data-text="No tags found"
              no-match-text="No tag matches found"
              style="width: 100%;")
              el-option#tag-options(
                v-for="item in tagsList"
                :key="item.id"
                :label="item.text"
                :value="item.id"
              )
        
        el-col(:span="12")
          // Custom Symbols
          el-form-item()
            label(:class="$style.label") Custom Symbols
            math-quill-input(
              v-model="newSymbol"
              :maxlength="uiSettings.SYMBOLS"
              @onMathInputEnter="addCustomSymbol"
              ref="customSymbol"
            )
            el-button(
              icon="el-icon-circle-plus-outline"
              @click="addCustomSymbol"
            ) Add    
          // Custom Symbols List
          el-form-item()
            div#placeholder-custom_symbols(v-if="custom_symbols.length === 0") 
                i Please add your custom symbols (Optional)
            div(v-else 
              v-for="sym, symIndex) in custom_symbols")
              el-form-item(prop="custom_symbols")
              div(style="display: flex; ")
                math-quill-view#mathquillview(:value="sym[1] || sym[0]")
                el-button(
                  slot="append" 
                  icon="el-icon-close" 
                  :id="custom_symbols.length"
                  @click="removeCustomSymbol(symIndex)")   

      // Buttons
      el-row(:gutter="20")
        el-col#buttons(:span="24" style="margin-bottom: 2%;")
          el-button-group(style="float: right;")
            el-button(
              class="normal-button"
              icon="el-icon-error"
              @click="onCancelClicked") Cancel
            el-button(
              class="primary-button"
              icon="el-icon-document"
              @click="onSubmitValidate('question')") Save                     
            
</template>

<script>
  import RichEditor from '@/components/utils/RichEditor'
  import MathQuillInput from '@/components/utils/MathQuillInput'
  import MathQuillView from '@/components/utils/MathQuillView'
  import utilMixin from '@/components/mixins/utilMixin'
  import {MAX_LENGTH} from '@/defines/constants'

  export default {
    name: 'AnswerInput',

    components: {
      RichEditor,
      MathQuillInput,
      MathQuillView,
    },  

    mixins: [utilMixin],

    props: {
      question: {
        type: Object,
        required: true,
        default: () => {},
      },            

      action: {
        type: String,
        required: true,
      },

      tagsList: {
        type: Array,
        default: () => { return [] },
      },
    },

    data() {
      return {
        answerInput: ['custom_symbols', 'allow_math_equivs', 'lefthand', 'righthand'],

        excludeData: ['createdAt', 'updatedAt'],
        custom_symbols: [],
        searchParams: [],
        newSymbol: '',

        uiSettings: MAX_LENGTH,
        rules: {
          title: [
            { required: true, message: 'Please input the Question\'s title', trigger: 'blur' },
            { min: 1, max: MAX_LENGTH.TITLE,
              message: 'Length should be 0 to ' + MAX_LENGTH.TITLE, trigger: 'blur' },
          ],
          text: [
            { required: true, message: 'Please input the Question text', trigger: 'blur' },
            { min: 1, max: MAX_LENGTH.CONTENT,
              message: 'Length should be 0 to ' + MAX_LENGTH.CONTENT, trigger: 'blur' },
          ],
          correct_answer: [
            { required: true, allowspecial: false, validator: this.mValidateText, trigger: 'blur' },
            { min: 1, max: MAX_LENGTH.TITLE,
              message: 'Length should be 0 to ' + MAX_LENGTH.TITLE, trigger: 'blur' },
          ],
          lefthand: [
            { allowspecial: false, validator: this.mValidateText, trigger: 'blur' },
            { min: 0, max: MAX_LENGTH.TITLE,
              message: 'Length should be 0 to ' + MAX_LENGTH.TITLE, trigger: 'blur' },
          ],
          righthand: [
            { allowspecial: false, validator: this.mValidateText, trigger: 'blur' },
            { min: 0, max: MAX_LENGTH.TITLE,
              message: 'Length should be 0 to ' + MAX_LENGTH.TITLE, trigger: 'blur' },
          ],
        },        
      }
    },

    mounted() {
      this.populateForm()
    },

    methods: {
      onSubmitValidate(formName) {
        this.$refs[formName].validate((valid) => {
          if (valid) {
            this.question.quiz_id = this.$store.state.quizData.id
            this.question.resource_id = this.$store.state.quizData.resourceId
            const validsymbols = []
            for (let i=0; i < this.custom_symbols.length; i += 1) {
              const sym = this.custom_symbols[i][0].trim()
              if (!validsymbols.includes(sym)) {
                validsymbols.push(sym)
              }
            }
            this.question.custom_symbols = validsymbols.sort()

            // Add tags
            if (this.searchParams.length > 0) {
              this.question.tags = []
              this.searchParams.forEach((item, index) => {
                this.question.tags.push(item)
              })
            }

            // Create new question
            this.question.isNew = (this.action === 'create') ? true : false
            this.$emit('onProcessing', true)
            this.axios.post('/api/quiz/' + this.question.quiz_id + '/question', this.question)
              .then((result) => {
                this.$emit('onProcessing', false)
                this.mNotification({message: result.data.message})
                this.question.id =  result.data.object.id
                this.$emit('onQuestionSaved')
              })
              .catch((error) => {
                this.mNotification({error: error, message: 'Your Question was not saved.'})
              })            
          } else {
            return false
          }
        })
      },

      onCancelClicked() {
        this.$refs.question.resetFields()
        this.custom_symbols = []
        this.$emit('formCancelClicked')
      },

      addCustomSymbol() {
        if (this.newSymbol.length === 0) {
          return
        }

        if (this.mHasSpecialTags(this.newSymbol)) {
          this.mNotification({
            type: 'info',
            info: 'Special html and script tags are not allowed.',            
          })
        } else if (this.newSymbol.length > MAX_LENGTH.SYMBOLS) {
          this.mNotification({
            type: 'info',
            info: 'Length should be 1 to ' + MAX_LENGTH.SYMBOLS,            
          })
        } else {
          this.custom_symbols.push([
            this.newSymbol,
            this.newSymbol,
            [this.newSymbol],
          ])
          this.newSymbol = ''
        }
      },

      removeCustomSymbol(index) {
        this.custom_symbols.splice(index, 1)
      },      

      populateForm() {
        if (this.question.custom_symbols.length > 0) {
          this.question.custom_symbols.forEach(item => {
            this.newSymbol = item
            this.addCustomSymbol()
          })
        }

        if (this.question.tags !== undefined && this.question.tags !== null) {
          this.question.tags.forEach((item, index) => {
            this.searchParams.push(item)
          })
        }
      },
    },
  }
</script>

<style module>
  #question-builder {
    margin: 2%;
    margin-bottom: 10px;
  }

  label {
    font-family: Arial, Helvetica, sans-serif;
    font-size: 14px;
    font-weight: bold;
    color: #757575;
  }    

  #form-builder {
    width: parent;
    height: 300px;
  }  

  #placeholder-custom_symbols {
    color: #C0C4CC;
    text-align: center; 
    border: 2px solid #DCDFE6; 
    border-style: dashed;
  }

  #mathquillview {
    -webkit-appearance: none;
    background-color: #FFF;
    border-radius: 4px;
    border: 1px solid #DCDFE6;
    -webkit-box-sizing: border-box;
    box-sizing: border-box;
    color: #606266;
    display: inline-block;   
    font-size: 16px;
    padding: 8px 15px 8px;
    width: 100%;
  }  
</style>
